import { useRef, type ReactNode } from 'react';
import { useOutsideClick } from './useOutsideClick';
import styles from './AppsFilterButton.module.css';
import menuStyles from './appsMenu.module.css';

/**
 * Which glyph the trigger carries. `funnel-only` renders no label text at
 * all — it is the compact per-column-header trigger, identified purely by
 * `ariaLabel`.
 */
export type AppsFilterIcon = 'add-leading' | 'chevron-trailing' | 'funnel-only';

interface AppsFilterButtonProps {
    label: string;
    /** Selected option's display label, rendered in ink after the muted `label`. */
    value?: string;
    /** Result count for the current selection — the design's dark pill. */
    count?: number;
    active: boolean;
    icon: AppsFilterIcon;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: ReactNode;
    /** Required when `icon === 'funnel-only'` — there is no visible label to name the button otherwise. */
    ariaLabel?: string;
}

function AddIcon({ active }: { active: boolean }) {
    return (
        <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
            className={active ? `${styles.btnIcon} is-on` : styles.btnIcon}
        >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v8M8 12h8" strokeLinecap="round" />
        </svg>
    );
}

function ChevronIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
            className={styles.btnIcon}
        >
            <path d="m6 9 6 6 6-6" strokeLinecap="round" />
        </svg>
    );
}

/** Exported so a column header can reuse it for its own filter-shortcut button (see `AppsTable`). */
export function FunnelIcon({ active }: { active: boolean }) {
    return (
        <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            aria-hidden="true"
            className={active ? `${styles.btnIcon} is-on` : styles.btnIcon}
        >
            <path d="M4 5h16l-6 7v5l-4 2v-7z" strokeLinejoin="round" />
        </svg>
    );
}

/**
 * EAA-1817: `/console/apps` (S6) toolbar filter trigger — a dashed pill that
 * goes solid once its facet is set. The idiom came from `/console/role-map`'s
 * own filter pill, COPIED rather than imported because that one was built on
 * antd's `Popover`, which this screen's constraints (no Ant Design here)
 * forbid; here the same trigger shape and menu-option contract are backed by a
 * native click-outside/Escape popover (`useOutsideClick`). That role-map pill
 * no longer exists — the screen moved to `DataTable`'s own `showToolbar` — so
 * this file is now the only implementation of the idiom.
 *
 * `open` is CONTROLLED from the view model (not local state) for the same
 * reason as the sibling: a column header's funnel icon is a second trigger
 * for the same "Trạng thái" menu the toolbar pill opens (see `AppsTable`'s
 * status column), and two independently-opening popovers under one name
 * would read as two different filters wearing the same label.
 */
export function AppsFilterButton({
    label,
    value,
    count,
    active,
    icon,
    open,
    onOpenChange,
    children,
    ariaLabel,
}: AppsFilterButtonProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    useOutsideClick(rootRef, open, () => onOpenChange(false));

    return (
        <div className={styles.filter} ref={rootRef}>
            <button
                type="button"
                className={active ? `${styles.btn} is-active` : styles.btn}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label={icon === 'funnel-only' ? ariaLabel : undefined}
                onClick={() => onOpenChange(!open)}
            >
                {icon === 'add-leading' && <AddIcon active={active} />}
                {icon === 'funnel-only' && <FunnelIcon active={active} />}
                {icon !== 'funnel-only' && (
                    <>
                        <span
                            className={
                                value
                                    ? `${styles.btnLabel} ${styles.btnLabelMuted}`
                                    : styles.btnLabel
                            }
                        >
                            {label}
                        </span>
                        {value && <span className={styles.btnValue}>{value}</span>}
                        {typeof count === 'number' && (
                            <span className={styles.btnCount}>{count}</span>
                        )}
                    </>
                )}
                {icon === 'chevron-trailing' && <ChevronIcon />}
            </button>
            {open && (
                <div className={styles.menuPopover} role="menu">
                    {children}
                </div>
            )}
        </div>
    );
}

/**
 * EAA-1817: one radio-style option row inside an `AppsFilterButton` menu — a
 * teal check when selected, an equal-width spacer otherwise so labels never
 * shift. Every facet on this screen (Trạng thái/Danh mục/Phạm vi) is
 * single-select with an implicit "Tất cả" (all) option that clears the facet —
 * one selected value per facet, never a multi-check list.
 */
export function AppsMenuOption({
    checked,
    label,
    count,
    onSelect,
}: {
    checked: boolean;
    label: string;
    count?: number;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            className={checked ? `${menuStyles.option} is-checked` : menuStyles.option}
            onClick={onSelect}
            role="menuitemradio"
            aria-checked={checked}
        >
            {checked ? (
                <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.4}
                    aria-hidden="true"
                    className={menuStyles.optionCheck}
                >
                    <path d="m5 13 4 4L19 7" strokeLinecap="round" />
                </svg>
            ) : (
                <span className={menuStyles.optionSpacer} aria-hidden="true" />
            )}
            <span className={menuStyles.optionLabel}>{label}</span>
            {typeof count === 'number' && <span className={menuStyles.optionCount}>{count}</span>}
        </button>
    );
}

// EAA-1817: the Mã/Tên per-column search popover that used to live here
// (`AppsColumnSearch`) was removed — `AppsTable` now renders those as the
// kit `DataTable`'s own native column-filter icon (`ColumnConfig.filter`,
// type `'text'`), which reads and writes the exact same `codeQuery`/
// `nameQuery` state in `useAppsTable`. See that file's and `AppsTable`'s
// doc comments for why.
